import React from "react";
import DevelopSpecializedSkillSetsAndConfidenceIcon from "@/components/icons/develop-specialized-skill-sets-and-confidence-icon";
import EngagingLearningEnvironmentsIcon from "@/components/icons/engaging-learning-environments-icon";
import SimulatesRealClientInteractionIcon from "@/components/icons/simulates-real-client-interaction-icon";
import ResourcesTextCard from "@/components/ui/resources-text-card";
import { IconName } from "@/lib/get-icon";

type Benefit = {
  iconName?: IconName;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
};

// Icon name to component mapping
const iconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  "simulates-real-client-interaction": SimulatesRealClientInteractionIcon,
  "develop-specialized-skill-sets":
    DevelopSpecializedSkillSetsAndConfidenceIcon,
  "engaging-learning-environments": EngagingLearningEnvironmentsIcon,
};

// Helper to get icon component from either iconName or icon prop
const getIconComponent = (
  benefit: Benefit
): React.FC<React.SVGProps<SVGSVGElement>> => {
  if (benefit.iconName && iconMap[benefit.iconName]) {
    return iconMap[benefit.iconName];
  }
  return benefit.icon!;
};

export interface BenefitsCIVRProps {
  id?: string;
  title?: string;
  benefits?: Benefit[];
}

export const defaultBenefitsCiVr: Benefit[] = [
  {
    iconName: "simulates-real-client-interaction" as IconName,
    title: "Simulates Real Client Interactions",
    text: "Experience real interactions with virtual clients to see how their demeanor changes based on your responses and interactions.",
  },
  {
    iconName: "develop-specialized-skill-sets" as IconName,
    title: "Develop Specialized Skill Sets & Confidence",
    text: "Master the skills you need to effectively care for older adults living with Alzheimer's Disease and similar cognitive impairments.",
  },
  {
    iconName: "engaging-learning-environments" as IconName,
    title: "Engaging Learning Environments",
    text: "Combining traditional classroom methodologies with real-life field experiences, create unforgettable training experiences.",
  },
];

const BenefitsCIVR: React.FC<BenefitsCIVRProps> = ({
  id,
  title = "Benefits",
  benefits = defaultBenefitsCiVr,
}) => {
  return (
    <section
      className="bg-white"
      id={id}
      aria-labelledby="benefits-heading"
    >
      <div className="mx-auto grid max-w-lg grid-cols-1 gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:gap-8 lg:px-3 lg:py-10">
        <h2
          id="benefits-heading"
          className="font-montserrat text-heading-4 text-primary-600 md:text-heading-3 lg:text-heading-2"
        >
          {title}
        </h2>
        <div
          className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-10"
          role="list"
        >
          {benefits?.map((benefit) => (
            <ResourcesTextCard
              cardType="icon-benefit"
              icon={getIconComponent(benefit)}
              title={benefit.title}
              text={benefit.text}
              key={benefit.title + " - " + benefit.text}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsCIVR;
